/**
 * SECTION 09 — Remediation Roadmap & Recommended Actions.
 *
 * Executive-grade view focused on "what should the organization do next?".
 * Findings are grouped by Priority (Immediate Actions / Quick Wins /
 * Strategic Initiatives) so each grouped table can drop the Priority column
 * and give more horizontal space to Finding and Recommended Action.
 */
import type jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { PAGE_MARGIN, REPORT_THEME, type RGB } from "./theme";
import { drawRunningHeader, drawPageFooter, drawSectionTitle } from "./pageChrome";

export interface RemediationRoadmapRow {
  display_id?: string | null;
  display_seq?: number | null;
  control_code?: string | null;
  finding_title?: string | null;
  risk_level?: string | null;
  priority_level?: string | null;
  timeframe?: string | null;
  recommended_action?: string | null;
  csv_domain?: string | null;
  nist_function?: string | null;
  due_date?: string | null;
}

export interface RemediationRoadmapContext {
  rows: RemediationRoadmapRow[];
  clientOrgName: string;
  methodology?: string | null;
  frameworks?: string[] | null;
  startPageNumber?: number;
}

// ── Sort weights ─────────────────────────────────────────────────────
const PRIORITY_WEIGHT: Record<string, number> = {
  Immediate: 0,
  "Quick Win": 1,
  Strategic: 2,
};
const RISK_WEIGHT: Record<string, number> = {
  Critical: 0,
  High: 1,
  Moderate: 2,
  Medium: 2,
  Low: 3,
};

// ── Badge palettes ───────────────────────────────────────────────────
const RISK_BADGE: Record<string, { fill: RGB; text: RGB }> = {
  Critical: { fill: [153, 27, 27], text: [255, 255, 255] },
  High:     { fill: [220, 38, 38], text: [255, 255, 255] },
  Moderate: { fill: [245, 158, 11], text: [255, 255, 255] },
  Low:      { fill: [148, 163, 184], text: [255, 255, 255] },
};

function normalizeRisk(v?: string | null): string {
  if (!v) return "—";
  const lower = String(v).trim().toLowerCase();
  if (lower === "critical") return "Critical";
  if (lower === "high") return "High";
  if (lower === "moderate" || lower === "medium") return "Moderate";
  if (lower === "low") return "Low";
  return String(v).trim();
}
function normalizePriority(v?: string | null): string {
  if (!v) return "—";
  const lower = String(v).trim().toLowerCase();
  if (lower === "immediate") return "Immediate";
  if (lower === "quick win") return "Quick Win";
  if (lower === "strategic") return "Strategic";
  return String(v).trim();
}

function dedupe(rows: RemediationRoadmapRow[]): RemediationRoadmapRow[] {
  const seen = new Set<string>();
  const unique: RemediationRoadmapRow[] = [];
  for (const r of rows) {
    const key = `${(r.finding_title ?? "").toLowerCase().trim()}|${(r.control_code ?? "").toLowerCase().trim()}`;
    if (seen.has(key)) continue;
    seen.add(key);
    unique.push(r);
  }
  return unique;
}

function sortWithinGroup(rows: RemediationRoadmapRow[]): RemediationRoadmapRow[] {
  const dueTs = (v?: string | null) => {
    if (!v) return Number.POSITIVE_INFINITY;
    const t = new Date(v).getTime();
    return isNaN(t) ? Number.POSITIVE_INFINITY : t;
  };
  return [...rows].sort((a, b) => {
    // 1) Due Date ASC (nulls last)
    const ta = dueTs(a.due_date);
    const tb = dueTs(b.due_date);
    if (ta !== tb) return ta - tb;
    // 2) Priority
    const pa = PRIORITY_WEIGHT[a.priority_level ?? ""] ?? 9;
    const pb = PRIORITY_WEIGHT[b.priority_level ?? ""] ?? 9;
    if (pa !== pb) return pa - pb;
    // 3) display_seq, then control_code as final tiebreakers
    const sa = a.display_seq ?? Number.POSITIVE_INFINITY;
    const sb = b.display_seq ?? Number.POSITIVE_INFINITY;
    if (sa !== sb) return sa - sb;
    return (a.control_code ?? "").localeCompare(b.control_code ?? "");
  });
}

/** Word-aware wrap: breaks on whitespace first, then on hyphens. */
function wrapWords(d: jsPDF, text: string, maxW: number): string[] {
  const lines: string[] = [];
  const paragraphs = String(text ?? "").split(/\r?\n/);
  for (const para of paragraphs) {
    const tokens = para.split(/\s+/).filter(Boolean);
    let current = "";
    const flush = () => { if (current) { lines.push(current); current = ""; } };
    const pushToken = (tok: string) => {
      if (!current) { current = tok; return; }
      const candidate = `${current} ${tok}`;
      if (d.getTextWidth(candidate) <= maxW) current = candidate;
      else { flush(); current = tok; }
    };
    for (const tok of tokens) {
      if (d.getTextWidth(tok) <= maxW) { pushToken(tok); continue; }
      const parts = tok.split("-");
      if (parts.length > 1) {
        for (let i = 0; i < parts.length; i++) {
          const seg = i < parts.length - 1 ? `${parts[i]}-` : parts[i];
          pushToken(seg);
        }
      } else {
        flush();
        lines.push(tok);
      }
    }
    flush();
  }
  return lines.length ? lines : [""];
}

function paintRiskBadge(doc: jsPDF, cell: any, label: string): void {
  const palette = RISK_BADGE[label];
  if (!palette) return;

  // Repaint cell background to mask default cell text rendering area.
  const bg = cell.styles.fillColor;
  if (bg) {
    doc.setFillColor(bg[0] ?? 255, bg[1] ?? 255, bg[2] ?? 255);
    doc.rect(cell.x + 0.5, cell.y + 0.5, cell.width - 1, cell.height - 1, "F");
  }

  const padX = 4;
  const bh = 12;
  doc.setFontSize(7.5);
  doc.setFont("helvetica", "bold");
  const tw = doc.getTextWidth(label);
  const bw = tw + padX * 2;
  const cx = cell.x + cell.width / 2;
  const cy = cell.y + cell.height / 2;
  const bx = cx - bw / 2;
  const by = cy - bh / 2;

  doc.setFillColor(...palette.fill);
  doc.roundedRect(bx, by, bw, bh, 2, 2, "F");
  doc.setTextColor(...palette.text);
  doc.text(label, cx, by + bh / 2 + 2.4, { align: "center" });
}

interface GroupSpec {
  key: "Immediate" | "Quick Win" | "Strategic";
  title: string;
  description: string;
  accent: RGB;
}

const GROUPS: GroupSpec[] = [
  {
    key: "Immediate",
    title: "Immediate Actions",
    description: "Address within the next 30–60 days to reduce near-term exposure.",
    accent: [220, 38, 38],
  },
  {
    key: "Quick Win",
    title: "Quick Wins",
    description: "High-impact improvements achievable in the next 60–120 days.",
    accent: [201, 162, 39],
  },
  {
    key: "Strategic",
    title: "Strategic Initiatives",
    description: "Longer-term investments to mature the cybersecurity program.",
    accent: [37, 99, 235],
  },
];

export function renderRemediationRoadmap(doc: jsPDF, ctx: RemediationRoadmapContext): void {
  const rows = Array.isArray(ctx.rows) ? ctx.rows : [];
  if (rows.length === 0) return;

  doc.addPage();
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const FOOTER_RESERVE = 56;
  const TOP_MARGIN_CONT = 120;

  drawRunningHeader(doc, {
    clientName: ctx.clientOrgName,
    frameworks: ctx.frameworks ?? null,
    methodology: ctx.methodology ?? null,
  });

  let y = drawSectionTitle(doc, {
    sectionNumber: "09",
    title: "Remediation Roadmap & Recommended Actions",
  });

  // Subtitle
  doc.setTextColor(...REPORT_THEME.textMuted.rgb);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  const subtitle =
    "The following remediation roadmap summarizes prioritized actions to address findings identified " +
    "during the assessment. Immediate actions should be addressed first to reduce near-term exposure, " +
    "while Quick Wins represent high-value improvements achievable with existing capacity and " +
    "Strategic Initiatives should be incorporated into longer-term security improvement plans.";
  const subLines = doc.splitTextToSize(subtitle, pageW - PAGE_MARGIN * 2);
  doc.text(subLines, PAGE_MARGIN, y, { lineHeightFactor: 1.4 } as any);
  y += subLines.length * 12 + 8;

  // Deduplicate findings and compute horizon counts (used by the
  // Implementation Roadmap timeline). KPI summary cards have been removed —
  // the timeline below the roadmap now serves as the executive summary.
  const deduped = dedupe(rows);
  const counts = {
    Immediate: deduped.filter((r) => normalizePriority(r.priority_level) === "Immediate").length,
    "Quick Win": deduped.filter((r) => normalizePriority(r.priority_level) === "Quick Win").length,
    Strategic: deduped.filter((r) => normalizePriority(r.priority_level) === "Strategic").length,
  };

  const borderGray: RGB = [156, 163, 175];
  const pageNumberAtTableStart = doc.getCurrentPageInfo().pageNumber;

  // ── Implementation Roadmap (renders directly under the introduction
  // on the same page; detailed tables start on the following page) ──
  y = renderImplementationRoadmap(doc, {
    deduped,
    counts,
    pageW,
    pageH,
    FOOTER_RESERVE,
    TOP_MARGIN_CONT,
    clientOrgName: ctx.clientOrgName,
    frameworks: ctx.frameworks ?? null,
    methodology: ctx.methodology ?? null,
    yStart: y,
  });

  // Detailed remediation tables begin on a fresh page so the executive
  // roadmap remains a clean single-page artifact.
  doc.addPage();
  drawRunningHeader(doc, {
    clientName: ctx.clientOrgName,
    frameworks: ctx.frameworks ?? null,
    methodology: ctx.methodology ?? null,
  });
  y = drawSectionTitle(doc, {
    sectionNumber: "09",
    title: "Remediation Roadmap & Recommended Actions",
    continued: true,
  });




  // Column widths (no Priority column inside grouped tables).
  // Domain 18% / Finding 26% / Risk 8% / Timeframe 12% / Action 36%.
  const usableW = pageW - PAGE_MARGIN * 2;
  const colW = {
    domain:    usableW * 0.18,
    finding:   usableW * 0.26,
    risk:      usableW * 0.08,
    timeframe: usableW * 0.12,
    action:    usableW * 0.36,
  };

  // Pre-wrap Domain text on whitespace/hyphen boundaries so autoTable never
  // splits mid-word. We measure with the same font/size used for the column.
  const domainPadX = 9 + 9;
  const domainContentW = colW.domain - domainPadX;
  const prewrapDomain = (raw: string): string => {
    const prev = (doc as any).internal.getFont?.();
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9.5);
    const lines = wrapWords(doc, raw, domainContentW);
    if (prev) doc.setFont(prev.fontName, prev.fontStyle);
    return lines.join("\n");
  };

  const drawGroupHeading = (group: GroupSpec, count: number, continued = false) => {
    const barY = y;
    const barH = 20;
    doc.setFillColor(...group.accent);
    doc.rect(PAGE_MARGIN, barY, 4, barH, "F");

    const textX = PAGE_MARGIN + 16; // 12pt gap between bar and text
    doc.setTextColor(...REPORT_THEME.navy.rgb);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    const titleLabel = continued
      ? `${group.title} (continued)`
      : `${group.title}  (${count})`;
    doc.text(titleLabel, textX, barY + 14);
    y += barH + 6;

    doc.setTextColor(...REPORT_THEME.textMuted.rgb);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text(group.description, textX, y);
    y += 14;
  };

  // ── Render one grouped table per priority bucket ──
  for (const group of GROUPS) {
    const groupRows = sortWithinGroup(
      deduped.filter((r) => normalizePriority(r.priority_level) === group.key)
    );
    if (groupRows.length === 0) continue;

    // Guarantee heading + at least one body row fits; otherwise new page.
    const headingNeeded = 40 + 44;
    if (y + headingNeeded > pageH - FOOTER_RESERVE) {
      doc.addPage();
      drawRunningHeader(doc, {
        clientName: ctx.clientOrgName,
        frameworks: ctx.frameworks ?? null,
        methodology: ctx.methodology ?? null,
      });
      y = drawSectionTitle(doc, {
        sectionNumber: "09",
        title: "Remediation Roadmap & Recommended Actions",
        continued: true,
      });
    }

    drawGroupHeading(group, groupRows.length, false);

    const body = groupRows.map((r) => {
      const title = (r.finding_title ?? "Untitled finding").trim();
      const idPrefix = r.display_id ? `[${r.display_id}] ` : "";
      return [
        prewrapDomain((r.csv_domain ?? "").trim() || "—"),
        `${idPrefix}${title}`,
        normalizeRisk(r.risk_level),
        (r.timeframe ?? "").trim() || "—",
        (r.recommended_action ?? "").trim() || "—",
      ];
    });

    const tableStartPage = doc.getCurrentPageInfo().pageNumber;

    autoTable(doc, {
      startY: y,
      margin: { left: PAGE_MARGIN, right: PAGE_MARGIN, bottom: FOOTER_RESERVE, top: TOP_MARGIN_CONT },
      rowPageBreak: "auto",
      showHead: "everyPage",
      head: [["Domain", "Finding", "Risk", "Timeframe", "Recommended Action"]],
      body,
      theme: "grid",
      styles: {
        font: "helvetica",
        fontSize: 9,
        cellPadding: { top: 9, right: 9, bottom: 9, left: 9 },
        textColor: REPORT_THEME.textPrimary.rgb as any,
        lineColor: borderGray as any,
        lineWidth: 0.7,
        valign: "top",
        overflow: "linebreak",
      },
      headStyles: {
        fillColor: REPORT_THEME.surfaceAlt.rgb as any,
        textColor: REPORT_THEME.navy.rgb as any,
        fontStyle: "bold",
        fontSize: 9,
        halign: "left",
        valign: "middle",
        cellPadding: { top: 9, right: 9, bottom: 9, left: 9 },
        lineColor: borderGray as any,
        lineWidth: 0.7,
        overflow: "visible",
      },
      alternateRowStyles: { fillColor: [250, 251, 253] as any },
      columnStyles: {
        0: { cellWidth: colW.domain, fontStyle: "bold", fontSize: 9.5, textColor: REPORT_THEME.navy.rgb as any },
        1: { cellWidth: colW.finding },
        2: { cellWidth: colW.risk, halign: "center", valign: "middle" },
        3: { cellWidth: colW.timeframe, halign: "center", valign: "middle" },
        4: { cellWidth: colW.action },
      },
      didDrawPage: (data) => {
        if (data.pageNumber > tableStartPage) {
          drawRunningHeader(doc, {
            clientName: ctx.clientOrgName,
            frameworks: ctx.frameworks ?? null,
            methodology: ctx.methodology ?? null,
          });
          drawSectionTitle(doc, {
            sectionNumber: "09",
            title: "Remediation Roadmap & Recommended Actions",
            continued: true,
          });
          y = TOP_MARGIN_CONT - 36;
          drawGroupHeading(group, groupRows.length, true);
          data.settings.startY = y;
          if (data.cursor) data.cursor.y = y;
        }
      },
      didDrawCell: (data) => {
        if (data.section !== "body") return;
        if (data.column.index === 2) {
          const value = String(data.cell.raw ?? "—");
          if (value === "—") return;
          paintRiskBadge(doc, data.cell, value);
        }
      },
    });

    y = ((doc as any).lastAutoTable?.finalY ?? y) + 22;
  }






  // Footers across all pages spanned by this section.
  const endPage = doc.getCurrentPageInfo().pageNumber;
  const startPageNumber = ctx.startPageNumber ?? pageNumberAtTableStart;
  for (let p = pageNumberAtTableStart, n = 0; p <= endPage; p++, n++) {
    doc.setPage(p);
    drawPageFooter(doc, startPageNumber + n);
  }
  doc.setPage(endPage);
}

// ─────────────────────────────────────────────────────────────────────
// Implementation Roadmap
//
// Executive summary of Section 09. Sits at the top of the section and
// answers three questions in ~10 seconds:
//   • What must we do first?
//   • What can be done next?
//   • How much work remains?
// ─────────────────────────────────────────────────────────────────────

interface ImplRoadmapCtx {
  deduped: RemediationRoadmapRow[];
  counts: Record<"Immediate" | "Quick Win" | "Strategic", number>;
  pageW: number;
  pageH: number;
  FOOTER_RESERVE: number;
  TOP_MARGIN_CONT: number;
  clientOrgName: string;
  frameworks: string[] | null;
  methodology: string | null;
  yStart: number;
}

// Executive roadmap display caps. Immediate Actions render in full
// (typically small); Quick Wins and Strategic Initiatives are capped so
// the roadmap remains a single-page executive artifact. The full set is
// always available in the detailed Section 09 tables that follow.
const IMMEDIATE_DISPLAY_CAP = 5;
const QUICK_WIN_DISPLAY_CAP = 5;
const STRATEGIC_DISPLAY_CAP = 5;


function renderImplementationRoadmap(doc: jsPDF, ctx: ImplRoadmapCtx): number {
  const { deduped, counts, pageW, pageH, FOOTER_RESERVE } = ctx;

  // Render directly under the Section 09 introduction on the same page —
  // no new page, no duplicate section title or intro paragraph.
  let y = ctx.yStart;

  // No-op (single-page guarantee — any unrenderable items roll into overflow).
  const ensureSpace = (_needed: number) => { void _needed; };

  // Extra breathing room between the Section 09 intro paragraph and the
  // Implementation Roadmap heading so it reads as a new visual subsection.
  y += 10;

  // Visual heading for the roadmap block.
  doc.setTextColor(...REPORT_THEME.navy.rgb);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.text("Implementation Roadmap", PAGE_MARGIN, y);
  y += 14;

  // Group rows preserving Section 09 ordering.
  const allGroups = GROUPS.map((g) => ({
    key: g.key,
    title: g.title,
    accent: g.accent,
    rows: sortWithinGroup(
      deduped.filter((r) => normalizePriority(r.priority_level) === g.key)
    ),
  }));

  // Apply per-bucket display caps; track overflow count for the +N line.
  const CAP_BY_KEY: Record<"Immediate" | "Quick Win" | "Strategic", number> = {
    Immediate: IMMEDIATE_DISPLAY_CAP,
    "Quick Win": QUICK_WIN_DISPLAY_CAP,
    Strategic: STRATEGIC_DISPLAY_CAP,
  };
  const LABEL_BY_KEY: Record<"Immediate" | "Quick Win" | "Strategic", string> = {
    Immediate: "Immediate Action",
    "Quick Win": "Quick Win",
    Strategic: "Strategic Initiative",
  };
  const groups = allGroups.map((g) => {
    const cap = CAP_BY_KEY[g.key];
    if (g.rows.length > cap) {
      return {
        ...g,
        displayRows: g.rows.slice(0, cap),
        overflow: g.rows.length - cap,
      };
    }
    return { ...g, displayRows: [...g.rows], overflow: 0 };
  });

  // ── Layout constants ──
  const cardGap = 12;
  const cardsW = pageW - PAGE_MARGIN * 2;
  const cardW = (cardsW - cardGap * 2) / 3;
  const cardPadX = 10;
  const cardPadY = 12;
  const headerH = 42;

  // Mini-card geometry — reserve dedicated rows so badges and domain text
  // never escape the card boundary.
  const MINI_PAD_X = 8;
  const MINI_PAD_TOP = 8;
  const MINI_PAD_BOTTOM = 8;
  const MINI_ID_H = 11;
  const MINI_TITLE_LH = 11;
  const MINI_META_GAP = 4;
  const MINI_META_H = 14; // dedicated row that holds domain text + risk badge

  // Measure title wrap height for a given finding.
  const measureItemHeight = (r: RemediationRoadmapRow): number => {
    const title = (r.finding_title ?? "Untitled finding").trim();
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    const innerW = cardW - cardPadX * 2 - MINI_PAD_X * 2;
    const titleLines = wrapWords(doc, title, innerW);
    return (
      MINI_PAD_TOP +
      MINI_ID_H +
      titleLines.length * MINI_TITLE_LH +
      MINI_META_GAP +
      MINI_META_H +
      MINI_PAD_BOTTOM
    );
  };

  // Pre-compute item heights per bucket.
  const itemHeights: number[][] = groups.map((g) =>
    g.displayRows.map(measureItemHeight)
  );

  // Render one mini-card for a finding inside a bucket card.
  const drawFindingMiniCard = (
    r: RemediationRoadmapRow,
    cx: number,
    yStart: number,
    width: number,
    height: number,
  ): void => {
    const id = (r.display_id ?? "—").toString();
    const title = (r.finding_title ?? "Untitled finding").trim();
    const domain = (r.csv_domain ?? "").trim();
    const risk = normalizeRisk(r.risk_level);

    // Outer mini card background.
    doc.setFillColor(252, 252, 253);
    doc.setDrawColor(...REPORT_THEME.border.rgb);
    doc.setLineWidth(0.4);
    doc.roundedRect(cx, yStart, width, height, 4, 4, "FD");

    const innerX = cx + MINI_PAD_X;
    const innerW = width - MINI_PAD_X * 2;
    let ty = yStart + MINI_PAD_TOP + 8; // text baseline for first line

    // Line 1: Finding ID
    doc.setTextColor(...REPORT_THEME.navy.rgb);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text(id, innerX, ty);
    ty += MINI_ID_H;

    // Title (wrapped, no truncation)
    doc.setTextColor(...REPORT_THEME.textPrimary.rgb);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    const titleLines = wrapWords(doc, title, innerW);
    titleLines.forEach((line) => {
      doc.text(line, innerX, ty);
      ty += MINI_TITLE_LH;
    });

    // Meta row — bounded inside the card, badge sits flush right and is
    // measured first so the domain text width never overlaps it.
    const metaTop = yStart + height - MINI_PAD_BOTTOM - MINI_META_H;
    const metaCenterY = metaTop + MINI_META_H / 2;

    // Risk badge (right side, fully inside card)
    let badgeLeftEdge = cx + width - MINI_PAD_X;
    const palette = RISK_BADGE[risk];
    if (palette) {
      doc.setFontSize(7);
      doc.setFont("helvetica", "bold");
      const padX = 4;
      const bh = 11;
      const tw = doc.getTextWidth(risk);
      const bw = tw + padX * 2;
      const bx = cx + width - MINI_PAD_X - bw;
      const by = metaCenterY - bh / 2;
      doc.setFillColor(...palette.fill);
      doc.roundedRect(bx, by, bw, bh, 1.5, 1.5, "F");
      doc.setTextColor(...palette.text);
      doc.text(risk, bx + bw / 2, by + bh / 2 + 2.4, { align: "center" });
      badgeLeftEdge = bx;
    }

    if (domain) {
      doc.setTextColor(...REPORT_THEME.textMuted.rgb);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.5);
      const domainMaxW = badgeLeftEdge - innerX - 6;
      const domLines = wrapWords(doc, domain, Math.max(20, domainMaxW));
      const label = domLines[0] ?? "";
      doc.text(label, innerX, metaCenterY + 3);
    }
  };

  // ── Draw bucket columns ──
  // Track remaining items + height arrays per bucket so we can paginate by
  // continuing ONLY the buckets that still have content (no empty cont'd
  // cards, no impossible counters).
  const remaining = groups.map((g, i) => ({
    bucket: g,
    items: [...g.displayRows],
    heights: [...itemHeights[i]],
    rendered: 0,
  }));

  // Reserve vertical budget for the execution timeline + section heading
  // ("Execution Timeline" label + node row + bottom spacing) so the entire
  // Implementation Roadmap stays on a single page. Extra spacing is reserved
  // above the Execution Timeline heading for visual separation.
  const TIMELINE_RESERVE = 16 /* label */ + 72 /* node */ + 22 /* spacing */ + 12 + 12 /* extra gap */;

  // Reserve a footer strip inside each bucket card for the +N overflow note
  // (only drawn when a bucket exceeds the display cap). The note wraps within
  // the card width so it never runs off the right edge; we reserve enough
  // vertical space for up to two wrapped lines.
  const OVERFLOW_NOTE_LH = 10;
  const OVERFLOW_NOTE_PAD = 6;
  const OVERFLOW_NOTE_MAX_LINES = 2;
  const OVERFLOW_NOTE_H = OVERFLOW_NOTE_LH * OVERFLOW_NOTE_MAX_LINES + OVERFLOW_NOTE_PAD;
  const anyOverflow = groups.some((g) => g.overflow > 0 || g.rows.length > g.displayRows.length);
  const overflowReserve = anyOverflow ? OVERFLOW_NOTE_H : 0;

  const available = pageH - FOOTER_RESERVE - y - 8 - TIMELINE_RESERVE;
  const bucketBodyCapacity = Math.max(60, available - headerH - cardPadY * 2 - overflowReserve);

  // Single-pass slice: take as many items as fit per bucket; the remainder
  // is folded into that bucket's overflow counter so the executive summary
  // never spills onto a second page.
  const slices = remaining.map((b) => {
    let used = 0;
    let count = 0;
    while (
      count < b.items.length &&
      used + b.heights[count] + (count > 0 ? 6 : 0) <= bucketBodyCapacity
    ) {
      used += b.heights[count] + (count > 0 ? 6 : 0);
      count += 1;
    }
    const dropped = b.items.length - count;
    if (dropped > 0) {
      b.bucket.overflow += dropped;
    }
    return { idx: remaining.indexOf(b), items: b.items.slice(0, count), bodyH: used };
  });

  const chunkBodyH = slices.reduce((m, s) => Math.max(m, s.bodyH), 0);
  const bodyH = headerH + cardPadY * 2 + Math.max(60, chunkBodyH) + overflowReserve;

  [0, 1, 2].forEach((i) => {
    const slice = slices[i];
    const b = remaining[i];
    const g = b.bucket;
    const cx = PAGE_MARGIN + i * (cardW + cardGap);
    const cy = y;
    const totalActual = g.rows.length;

    // Outer card
    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(...REPORT_THEME.border.rgb);
    doc.setLineWidth(0.6);
    doc.roundedRect(cx, cy, cardW, bodyH, 6, 6, "FD");

    // Accent top stripe
    doc.setFillColor(...g.accent);
    doc.roundedRect(cx, cy, cardW, 4, 2, 2, "F");

    // Header
    doc.setTextColor(...REPORT_THEME.navy.rgb);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text(g.title, cx + cardPadX, cy + 22);

    doc.setTextColor(...REPORT_THEME.textMuted.rgb);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    const findingNoun = totalActual === 1 ? "finding" : "findings";
    const findingVerb = totalActual === 1 ? "was" : "were";
    const subLabel =
      totalActual === 0
        ? "No findings recorded"
        : `${totalActual} ${findingNoun} ${findingVerb} recorded`;
    doc.text(subLabel, cx + cardPadX, cy + 36);

    // Divider
    doc.setDrawColor(...REPORT_THEME.border.rgb);
    doc.line(cx + cardPadX, cy + headerH, cx + cardW - cardPadX, cy + headerH);

    // Body
    let ry = cy + headerH + cardPadY;
    const innerX = cx + cardPadX;
    const innerW = cardW - cardPadX * 2;

    if (b.items.length === 0) {
      doc.setTextColor(...REPORT_THEME.navy.rgb);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      const emptyTitle = `No ${g.title} Identified`;
      doc.text(emptyTitle, cx + cardW / 2, ry + 16, { align: "center" });

      doc.setTextColor(...REPORT_THEME.textMuted.rgb);
      doc.setFont("helvetica", "italic");
      doc.setFontSize(8.5);
      const emptyBody =
        g.key === "Strategic"
          ? "Current findings are concentrated within Immediate and Quick-Win horizons. No remediation items currently require a long-term implementation horizon."
          : `No findings are currently scoped to the ${g.title.toLowerCase()} horizon.`;
      const emptyLines = doc.splitTextToSize(emptyBody, innerW - 8);
      let ey = ry + 32;
      emptyLines.forEach((ln: string) => {
        doc.text(ln, cx + cardW / 2, ey, { align: "center" });
        ey += 11;
      });
    } else {
      slice.items.forEach((r, idx) => {
        const h = b.heights[idx];
        drawFindingMiniCard(r, innerX, ry, innerW, h);
        ry += h + 6;
      });

      // +N overflow note — only when the bucket exceeds its display cap.
      // Pinned to the bottom of the bucket card so all columns align and
      // the Execution Timeline placement is unaffected.
      if (g.overflow > 0) {
        const label =
          g.overflow === 1
            ? `+1 additional finding is included in the detailed remediation tables below.`
            : `+${g.overflow} additional findings are included in the detailed remediation tables below.`;
        doc.setTextColor(...REPORT_THEME.textMuted.rgb);
        doc.setFont("helvetica", "italic");
        doc.setFontSize(8);
        // Wrap inside the card so the note never overflows horizontally.
        const wrapped: string[] = doc.splitTextToSize(label, innerW);
        const lines = wrapped.slice(0, OVERFLOW_NOTE_MAX_LINES);
        const baseY = cy + bodyH - OVERFLOW_NOTE_PAD - (lines.length - 1) * OVERFLOW_NOTE_LH;
        lines.forEach((ln, i) => {
          doc.text(ln, innerX, baseY + i * OVERFLOW_NOTE_LH);
        });
      }
    }
  });

  // Extra breathing room between the roadmap cards and the Execution
  // Timeline heading so they read as distinct subsections.
  y += bodyH + 28;


  // ── Horizontal timeline with dynamic counts ──
  const timelineH = 110;
  ensureSpace(timelineH);

  doc.setTextColor(...REPORT_THEME.navy.rgb);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("Execution Timeline", PAGE_MARGIN, y);
  y += 16;

  const tlW = pageW - PAGE_MARGIN * 2;
  const arrowW = 30;
  const nodeW = (tlW - arrowW * 2) / 3;
  const nodeH = 72;
  const nodeY = y;

  const horizons = [
    {
      title: "Immediate Actions",
      window: "0–90 Days",
      accent: GROUPS[0].accent,
      count: counts.Immediate,
    },
    {
      title: "Quick Wins",
      window: "90–180 Days",
      accent: GROUPS[1].accent,
      count: counts["Quick Win"],
    },
    {
      title: "Strategic Initiatives",
      window: "180+ Days",
      accent: GROUPS[2].accent,
      count: counts.Strategic,
    },
  ];

  horizons.forEach((h, i) => {
    const nx = PAGE_MARGIN + i * (nodeW + arrowW);
    doc.setFillColor(...REPORT_THEME.surfaceAlt.rgb);
    doc.setDrawColor(...h.accent);
    doc.setLineWidth(1.2);
    doc.roundedRect(nx, nodeY, nodeW, nodeH, 6, 6, "FD");

    // Accent left stripe
    doc.setFillColor(...h.accent);
    doc.rect(nx, nodeY, 3, nodeH, "F");

    doc.setTextColor(...REPORT_THEME.navy.rgb);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10.5);
    doc.text(h.title, nx + nodeW / 2, nodeY + 20, { align: "center" });

    doc.setTextColor(...REPORT_THEME.navy.rgb);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(15);
    doc.text(String(h.count), nx + nodeW / 2, nodeY + 42, { align: "center" });

    doc.setTextColor(...REPORT_THEME.textMuted.rgb);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    const findingsLabel = `${h.count === 1 ? "Finding" : "Findings"}`;
    doc.text(findingsLabel, nx + nodeW / 2, nodeY + 54, { align: "center" });

    doc.setFontSize(8.5);
    doc.text(h.window, nx + nodeW / 2, nodeY + 66, { align: "center" });

    if (i < horizons.length - 1) {
      const ax = nx + nodeW;
      const ay = nodeY + nodeH / 2;
      doc.setDrawColor(...REPORT_THEME.accent.rgb);
      doc.setLineWidth(1.4);
      doc.line(ax + 4, ay, ax + arrowW - 8, ay);
      const tipX = ax + arrowW - 6;
      doc.setFillColor(...REPORT_THEME.accent.rgb);
      doc.triangle(tipX, ay, tipX - 6, ay - 4, tipX - 6, ay + 4, "F");
    }
  });

  y = nodeY + nodeH + 22;
  return y;
}


